import React, { useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { Progress, Spin, Tag, Tooltip } from 'antd';
import {
  ApartmentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  RightOutlined,
  WarningFilled
} from '@ant-design/icons';
import styled, { keyframes } from 'styled-components';
import { IPipelineSummary } from '../../../core/modules/pipelines/pipelines.model';
import { useServices } from '../../../core/services/useServices';
import StatusBadge from './StatusBadge';
import { prefetchPipelineDetail } from './detail-views';
import { annotateScheduleWithLocal, formatRelative } from '../utils/formatStage';
import { BRENDA_GREEN, PIPELINE_STATUS_HEX } from '../utils/statusColors';

const Card = styled.button<{ $accent: string }>`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-left: 3px solid ${({ $accent }) => $accent};
  border-radius: 10px;
  padding: 16px 18px;
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: transform 0.12s, box-shadow 0.12s, border-color 0.12s;
  position: relative;

  &:hover {
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.07);
    transform: translateY(-2px);
    border-color: ${BRENDA_GREEN}66;
    border-left-color: ${({ $accent }) => $accent};
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const Group = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
  font-weight: 600;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const InfoIcon = styled(InfoCircleOutlined)`
  color: rgba(0, 0, 0, 0.35);
  font-size: 13px;
  flex-shrink: 0;
  transition: color 0.15s ease;

  &:hover {
    color: ${BRENDA_GREEN};
  }
`;

const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

// Cluster the progress bar + footer at card bottom so cards line up.
const BottomStack = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f5f5f5;
  padding-top: 10px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
`;

const Chevron = styled.span`
  color: rgba(0, 0, 0, 0.35);
  font-size: 11px;
`;

const FailureBadge = styled.span`
  color: #cf1322;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ResolvingBadge = styled.span`
  color: #1890ff;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const fetchSweep = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const FetchStrip = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  overflow: hidden;
  border-top-right-radius: 10px;

  &::after {
    content: '';
    display: block;
    width: 40%;
    height: 100%;
    background: linear-gradient(90deg, transparent, ${BRENDA_GREEN}, transparent);
    animation: ${fetchSweep} 1.4s ease-in-out infinite;
  }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.55; }
`;

const PulseLine = styled.span`
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const groupLabelFromId = (id: string): string => {
  if (id.startsWith('azureml.')) {
    return 'Model Training';
  }

  if (id.startsWith('durable.etl.') || id.startsWith('function.etl.')) {
    return 'ETL';
  }

  if (id.startsWith('durable.inference.')) {
    return 'Inference';
  }

  if (id.startsWith('timer.')) {
    return 'Timer';
  }

  if (id.startsWith('function.backend.')) {
    return 'Backend';
  }

  if (id.startsWith('function.')) {
    return 'Function';
  }

  return 'Pipeline';
};

interface IPipelineCardProps {
  summary: IPipelineSummary;
  isFetching?: boolean;
  onSelect: (id: string) => void;
}

const PipelineCard: React.FC<IPipelineCardProps> = ({ summary, isFetching, onSelect }) => {
  const isResolving = Boolean(
    isFetching
    && summary.latestRunStatus === 'unknown'
    && !summary.latestRunStartedAt
  );

  const queryClient = useQueryClient();
  const { pipelines: pipelinesService } = useServices();
  const prefetchedRef = useRef(false);

  // Single-fire detail prefetch on hover/focus.
  const handleMouseEnter = useCallback(() => {
    if (prefetchedRef.current || isFetching) {
      return;
    }
    prefetchedRef.current = true;
    prefetchPipelineDetail(summary.id, queryClient, pipelinesService);
  }, [summary.id, queryClient, pipelinesService, isFetching]);

  const accent = PIPELINE_STATUS_HEX[summary.latestRunStatus] ?? '#bfbfbf';
  const isTimer = summary.type === 'timer';
  const progressPercent = summary.latestRunStatus === 'success'
    ? 100
    : summary.latestRunStatus === 'warning'
      ? 100
      : summary.latestRunStatus === 'running'
        ? 50
        : summary.latestRunStatus === 'failed'
          ? 100
          : 0;

  const lastRunLabel = summary.latestRunStartedAt
    ? formatRelative(summary.latestRunStartedAt)
    : isTimer
      ? 'No runs tracked'
      : 'No runs';

  return (
    <Card
      type='button'
      $accent={accent}
      onMouseEnter={handleMouseEnter}
      onFocus={handleMouseEnter}
      onClick={() => onSelect(summary.id)}
    >
      {isFetching && <FetchStrip />}
      <TopRow>
        <TitleBlock>
          <Group>{groupLabelFromId(summary.id)}</Group>
          <Name>
            {summary.name}
            {summary.description && (
              <Tooltip title={summary.description} overlayStyle={{ maxWidth: 360 }}>
                <InfoIcon
                  onClick={(e) => e.stopPropagation()}
                  aria-label='What this pipeline does'
                />
              </Tooltip>
            )}
          </Name>
        </TitleBlock>
        {isTimer
          ? (
            <Tag
              color={BRENDA_GREEN}
              icon={<CheckCircleOutlined />}
              style={{ margin: 0 }}
            >
              Scheduled
            </Tag>
          )
          : isResolving
            ? (
              <Tag
                icon={<Spin indicator={<LoadingOutlined spin style={{ fontSize: 11 }} />} size='small' />}
                style={{ margin: 0, color: '#1890ff', borderColor: '#91d5ff', background: '#e6f7ff' }}
              >
                <span style={{ marginLeft: 6 }}>Loading</span>
              </Tag>
            )
            : <StatusBadge status={summary.latestRunStatus} />}
      </TopRow>

      <Meta>
        <MetaItem>
          <ClockCircleOutlined />
          {annotateScheduleWithLocal(summary.schedule)}
        </MetaItem>
        <MetaItem>
          {isTimer
            ? <>Next: {summary.nextExpectedAt ? formatRelative(summary.nextExpectedAt) : '—'}</>
            : isResolving
              ? <PulseLine>Last run: fetching…</PulseLine>
              : <>Last run: {lastRunLabel}</>}
        </MetaItem>
        {(summary.stagesCount ?? 0) > 1 && (
          <Tooltip title={`${summary.stagesCount} sub-pipelines run sequentially`}>
            <MetaItem style={{ color: '#1890ff', fontWeight: 500 }}>
              <ApartmentOutlined />
              {summary.stagesCount} stages
            </MetaItem>
          </Tooltip>
        )}
      </Meta>

      <BottomStack>
        {!isTimer && (
          <Progress
            percent={progressPercent}
            showInfo={false}
            size='small'
            status={
              summary.latestRunStatus === 'failed'
                ? 'exception'
                : summary.latestRunStatus === 'running'
                  ? 'active'
                  : progressPercent === 100
                    ? 'success'
                    : 'normal'
            }
            strokeColor={summary.latestRunStatus === 'failed' ? undefined : accent}
          />
        )}

        <Footer>
          <span>
            {(() => {
              if (isTimer) {
                return <span style={{ color: 'rgba(0,0,0,0.45)' }}>Standalone timer</span>;
              }

              if (summary.latestRunStatus === 'failed' || summary.errorMessage) {
                return (
                  <Tooltip title={summary.errorMessage || 'Latest run failed'}>
                    <FailureBadge>
                      <WarningFilled />
                      Latest failed
                    </FailureBadge>
                  </Tooltip>
                );
              }

              if (summary.latestRunStatus === 'warning') {
                return (
                  <Tooltip title={summary.errorMessage || 'Latest run completed with warnings'}>
                    <span style={{ color: '#faad14', fontWeight: 600 }}>
                      <WarningFilled /> Warning
                    </span>
                  </Tooltip>
                );
              }

              if (summary.latestRunStatus === 'unknown') {
                if (isResolving) {
                  return (
                    <ResolvingBadge>
                      <Spin indicator={<LoadingOutlined spin style={{ fontSize: 11 }} />} size='small' />
                      Loading…
                    </ResolvingBadge>
                  );
                }

                return <span style={{ color: 'rgba(0,0,0,0.45)' }}>No data</span>;
              }

              return <span>Healthy</span>;
            })()}
          </span>
          <Chevron>View detail <RightOutlined /></Chevron>
        </Footer>
      </BottomStack>
    </Card>
  );
};

export default React.memo(PipelineCard);
